/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { workloadIdentitiesWithdrawIssuer } from "../funcs/workloadIdentitiesWithdrawIssuer.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { WorkloadIdentityPolicy } from "../models/components/workloadidentitypolicy.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  WithdrawWorkloadIssuerRequest,
  WithdrawWorkloadIssuerSecurity,
} from "../models/operations/withdrawworkloadissuer.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type WithdrawWorkloadIssuerMutationVariables = {
  request: WithdrawWorkloadIssuerRequest;
  security?: WithdrawWorkloadIssuerSecurity | undefined;
  options?: RequestOptions;
};

export type WithdrawWorkloadIssuerMutationData = WorkloadIdentityPolicy;

export type WithdrawWorkloadIssuerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * withdrawIssuer workloadIdentities
 *
 * @remarks
 * Stop trusting an issuer. Every subject admitted under it is withdrawn in the same transaction, so no admission can outlive the issuer it names. Requires workload:write.
 */
export function useWithdrawWorkloadIssuerMutation(
  options?: MutationHookOptions<
    WithdrawWorkloadIssuerMutationData,
    WithdrawWorkloadIssuerMutationError,
    WithdrawWorkloadIssuerMutationVariables
  >,
): UseMutationResult<
  WithdrawWorkloadIssuerMutationData,
  WithdrawWorkloadIssuerMutationError,
  WithdrawWorkloadIssuerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildWithdrawWorkloadIssuerMutation(client, options),
    ...options,
  });
}

export function mutationKeyWithdrawWorkloadIssuer(): MutationKey {
  return ["@gram/client", "workloadIdentities", "withdrawIssuer"];
}

export function buildWithdrawWorkloadIssuerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: WithdrawWorkloadIssuerMutationVariables,
  ) => Promise<WithdrawWorkloadIssuerMutationData>;
} {
  return {
    mutationKey: mutationKeyWithdrawWorkloadIssuer(),
    mutationFn: function withdrawWorkloadIssuerMutationFn({
      request,
      security,
      options,
    }): Promise<WithdrawWorkloadIssuerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(workloadIdentitiesWithdrawIssuer(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
